import * as React from 'react';
import {Star} from 'lucide-react';
import {useTranslation} from 'react-i18next';
import {cn} from '../lib/cn';

export type RatingStarsProps = {
  rating: number;
  maxRating?: number;
  size?: number;
  showText?: boolean;
  reviewCount?: number;
  reviewsLabel?: string;
  description?: string;
  className?: string;
  starClassName?: string;
  filledColor?: string;
  emptyColor?: string;
};

export function RatingStars({
  rating,
  maxRating = 5,
  size = 20,
  showText = false,
  reviewCount,
  reviewsLabel,
  description,
  className,
  starClassName,
  filledColor = 'var(--chekin-amber-500)',
  emptyColor = 'var(--chekin-color-gray-3)',
}: RatingStarsProps) {
  const {t} = useTranslation();
  const normalizedRating = Math.max(0, Math.min(maxRating, rating));
  const stars = Array.from({length: maxRating}, (_, index) => index + 1);

  const componentId = React.useId();
  const decimal = normalizedRating - Math.floor(normalizedRating);
  const partialStarIndex = decimal > 0 ? Math.ceil(normalizedRating) : -1;
  const gradientId = `star-gradient-${componentId.replace(/:/g, '')}`;

  const getStarFill = (starIndex: number): string => {
    if (starIndex <= Math.floor(normalizedRating)) {
      return filledColor;
    }

    if (starIndex === partialStarIndex && decimal > 0) {
      return `url(#${gradientId})`;
    }

    return emptyColor;
  };

  return (
    <div className={cn('flex flex-col items-start gap-2', className)}>
      {showText && (
        <p className="text-4xl font-medium">
          {normalizedRating}/{maxRating}
        </p>
      )}

      <div className="flex items-center gap-1">
        <svg width="0" height="0" style={{position: 'absolute'}}>
          <defs>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset={`${decimal * 100}%`} stopColor={filledColor} />
              <stop offset={`${decimal * 100}%`} stopColor={emptyColor} />
            </linearGradient>
          </defs>
        </svg>

        {stars.map(star => (
          <Star
            key={star}
            size={size}
            className={cn('shrink-0', starClassName)}
            fill={getStarFill(star)}
            stroke="none"
            aria-hidden="true"
          />
        ))}
      </div>

      {(reviewCount !== undefined || description) && (
        <div className="flex flex-col font-medium text-[var(--chekin-color-gray-1)]">
          {reviewCount !== undefined && (
            <p className="text-xs leading-4">
              {reviewCount} {reviewsLabel || t('reviews')}
            </p>
          )}
          {description && <p className="text-sm leading-6">{description}</p>}
        </div>
      )}
    </div>
  );
}
